/**
 * Inventory API Client
 * Epic 9: Készlet nyilvántartás
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type InventoryItemType = 'PRODUCT' | 'RENTAL_EQUIPMENT' | 'PART' | 'CONSUMABLE';

/** Purchase category for goods receipt items (O-INV-1, INV-FG-3-2: 6 kategória) */
export type PurchaseCategory = 'ARU' | 'BERUHAZAS' | 'BERENDEZES' | 'EGYEB' | 'REZSI' | 'BERGEP';

/**
 * Cikkcsoport (INV-FG-1-2, ERP-TESZT-26).
 *
 * ⚠️ A tag-halmaz a `ProductGroupCode` Prisma-enum MÁSOLATA
 * (`apps/kgc-api/prisma/schema/product.prisma`) — a sorrend is onnan jön.
 * Az egyezést az `InventoryListPage.product-group-parity.spec.ts` gépiesen
 * asserteli a séma-fájl ellen, mert a QA-29 pont ennek a másolatnak az
 * elmaradt karbantartásából állt elő: az enum 11 tagra bővült, a másolat
 * 8-on maradt, és semmi nem jelzett.
 */
export type ProductGroupCode =
  | 'ALKATRESZ'
  | 'GEP'
  | 'TARTOZEK'
  | 'SZERSZAM'
  | 'MUNKARUHA'
  | 'VEGYSZER'
  | 'EGYEB'
  | 'BERGEP'
  | 'DIJAK'
  | 'SZERZODESES'
  | 'BERENDEZES';

/**
 * Per-warehouse stock breakdown (Phase-2, byWarehouse[] — #665 BE-contract).
 *
 * A parent {@link FourFieldStockSummary} raktáronkénti bontása. A mezőnevek
 * 1:1 a parent-tel (`*Quantity`) → ugyanaz a cell-render. Sum-invariáns:
 * parent = Σ byWarehouse. A BE raktárnév-rendezve adja vissza;
 * `warehouseId`-szűrővel 1 entry, anélkül minden raktár.
 */
export interface WarehouseStockBreakdown {
  warehouseId: string;
  warehouseName: string;
  totalQuantity: number;
  availableQuantity: number;
  reservedQuantity: number;
  inTransitQuantity: number;
  inServiceQuantity: number;
  rentedQuantity: number;
}

/** Four-field stock summary (INV-FG-1-1) */
export interface FourFieldStockSummary {
  tenantId: string;
  warehouseId?: string;
  productId: string;
  productName: string;
  totalQuantity: number;
  /** Szabad (Free) */
  availableQuantity: number;
  /** Rendelésre foglalt (Order-reserved) */
  reservedQuantity: number;
  /** Szállítás alatt / Rendelés alatt (In-transit / On-order) */
  inTransitQuantity: number;
  /** Szervizben (In-service) */
  inServiceQuantity: number;
  /** Kiadva/Bérelve */
  rentedQuantity: number;
  unit: string;
  minStockLevel?: number;
  stockLevelStatus: 'OK' | 'LOW' | 'CRITICAL' | 'OUT_OF_STOCK';
  lastUpdated: string;
  /**
   * Készletérték: quantity × purchasePrice (tenant-szintű összeg).
   * Opcionális — a read-model bővítés pending (BE-oldal), addig undefined.
   * Ha undefined, a FE "—"-t renderel (measured-honesty).
   */
  totalValue?: number | undefined;
  /**
   * Raktáronkénti bontás (Phase-2, #665). Opcionális — a BE read-model
   * `byWarehouse[]`-t ad (warehouseId-szűrő nélkül minden raktár, szűrővel 1).
   * Ha undefined/üres, a sor nem kibontható (measured-honesty).
   */
  byWarehouse?: WarehouseStockBreakdown[] | undefined;
}

/** Inventory group with RBAC visibility (O-INV-2) */
export type InventoryGroupCode =
  | 'ARUKESZLET'
  | 'BERGEPEK'
  | 'EGYEB'
  | 'SAJAT_ESZKOZ'
  | 'REZSI'
  | 'BERUHAZAS'
  | 'ECS';

export interface InventoryGroupInfo {
  code: InventoryGroupCode;
  name: string;
  visibleTo: string[];
}
export type InventoryStatus =
  | 'AVAILABLE'
  | 'RESERVED'
  | 'IN_TRANSIT'
  | 'IN_SERVICE'
  | 'SOLD'
  | 'RENTED'
  | 'DAMAGED'
  | 'LOST'
  | 'SCRAPPED';

export interface InventoryItem {
  id: string;
  tenantId: string;
  warehouseId: string;
  productId: string;
  type: InventoryItemType;
  status: InventoryStatus;
  serialNumber?: string;
  batchNumber?: string;
  locationCode?: string;
  quantity: number;
  reservedQuantity: number;
  unit: string;
  minStockLevel?: number;
  maxStockLevel?: number;
  purchasePrice?: number;
  inventoryGroup?: InventoryGroupCode;
  createdAt: string;
  updatedAt: string;
  // Joined fields
  productName?: string;
  productSku?: string;
  warehouseName?: string;
}

export interface InventoryListResponse {
  items: InventoryItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface InventoryResponse {
  data: InventoryItem;
}

export interface StockSummary {
  productId: string;
  totalQuantity: number;
  availableQuantity: number;
  reservedQuantity: number;
  inTransit: number;
  byWarehouse: Array<{
    warehouseId: string;
    warehouseName: string;
    quantity: number;
    available: number;
  }>;
}

export interface LowStockAlert {
  id: string;
  productId: string;
  productName: string;
  currentStock: number;
  minStockLevel: number;
  warehouseId: string;
  warehouseName: string;
}

export interface InventoryFilters {
  warehouseId?: string;
  productId?: string;
  type?: InventoryItemType;
  status?: InventoryStatus;
  serialNumber?: string;
  search?: string;
  inventoryGroup?: InventoryGroupCode;
  groupCode?: ProductGroupCode;
  offset?: number;
  limit?: number;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get list of inventory items with filters
 */
export async function getInventory(filters: InventoryFilters = {}): Promise<InventoryListResponse> {
  const params = new URLSearchParams();

  if (filters.warehouseId) params.append('warehouseId', filters.warehouseId);
  if (filters.productId) params.append('productId', filters.productId);
  if (filters.type) params.append('type', filters.type);
  if (filters.status) params.append('status', filters.status);
  if (filters.serialNumber) params.append('serialNumber', filters.serialNumber);
  if (filters.search) params.append('search', filters.search);
  if (filters.inventoryGroup) params.append('inventoryGroup', filters.inventoryGroup);
  if (filters.groupCode) params.append('groupCode', filters.groupCode);
  if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/inventory?${queryString}` : '/inventory';

  return api.get<InventoryListResponse>(endpoint);
}

/**
 * Get single inventory item by ID
 */
export async function getInventoryById(id: string): Promise<InventoryResponse> {
  return api.get<InventoryResponse>(`/inventory/${id}`);
}

/**
 * Get stock summary for a product
 */
export async function getStockSummary(
  productId: string,
  warehouseId?: string
): Promise<{ data: StockSummary }> {
  const params = warehouseId ? `?warehouseId=${warehouseId}` : '';
  return api.get<{ data: StockSummary }>(`/inventory/stock-summary/${productId}${params}`);
}

/**
 * Get bulk stock summaries — four-field view (INV-FG-1-1)
 * Returns: Szabad, Rendelésre foglalt, Szervizre foglalt, Rendelés alatt
 */
export async function getStockSummaries(
  warehouseId?: string,
  groupCode?: ProductGroupCode
): Promise<{ data: FourFieldStockSummary[] }> {
  const params = new URLSearchParams();
  if (warehouseId) params.append('warehouseId', warehouseId);
  if (groupCode) params.append('groupCode', groupCode);
  const query = params.toString();
  return api.get<{ data: FourFieldStockSummary[] }>(
    `/inventory/stock-summaries${query ? `?${query}` : ''}`
  );
}

/**
 * Get low stock alerts
 */
export async function getLowStockAlerts(warehouseId?: string): Promise<{ data: LowStockAlert[] }> {
  const params = warehouseId ? `?warehouseId=${warehouseId}` : '';
  return api.get<{ data: LowStockAlert[] }>(`/inventory/alerts/low-stock${params}`);
}

/**
 * Create inventory item
 */
export async function createInventoryItem(data: {
  warehouseId: string;
  productId: string;
  type: InventoryItemType;
  status?: InventoryStatus;
  serialNumber?: string;
  batchNumber?: string;
  locationCode?: string;
  quantity: number;
  unit: string;
  minStockLevel?: number;
  maxStockLevel?: number;
  purchasePrice?: number;
}): Promise<InventoryResponse> {
  return api.post<InventoryResponse>('/inventory', data);
}

/**
 * Update inventory item
 */
export async function updateInventoryItem(
  id: string,
  data: {
    warehouseId?: string;
    status?: InventoryStatus;
    locationCode?: string;
    quantity?: number;
    minStockLevel?: number;
    maxStockLevel?: number;
    purchasePrice?: number;
  }
): Promise<InventoryResponse> {
  return api.patch<InventoryResponse>(`/inventory/${id}`, data);
}

/**
 * Delete inventory item (soft delete)
 */
export async function deleteInventoryItem(id: string): Promise<void> {
  return api.delete<void>(`/inventory/${id}`);
}

/**
 * Adjust inventory quantity
 */
export async function adjustQuantity(
  id: string,
  adjustment: number,
  reason?: string
): Promise<InventoryResponse> {
  return api.post<InventoryResponse>(`/inventory/${id}/adjust`, { adjustment, reason });
}

// ============================================
// WAREHOUSE API
// ============================================

export interface Warehouse {
  id: string;
  name: string;
  code: string;
  address?: string;
  isDefault: boolean;
  createdAt: string;
}

/**
 * Get list of warehouses
 */
export async function getWarehouses(): Promise<{ data: Warehouse[] }> {
  return api.get<{ data: Warehouse[] }>('/warehouses');
}

// ============================================
// MOVEMENTS API
// ============================================

export interface StockMovementRecord {
  id: string;
  itemId: string;
  itemName: string;
  itemSku: string;
  type: string; // IN | OUT | ADJUSTMENT | TRANSFER | RETURN
  quantity: number;
  previousStock: number;
  newStock: number;
  reference?: string;
  note?: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  /**
   * Egységköltség a mozgáshoz (value-trail).
   * Opcionális — BE value-trail bővítés pending. Ha undefined, "—" renderelendő.
   * RENTAL típusnál szándékosan nem renderelendő (measured-honesty, Lexia-recon-ig).
   */
  unitCost?: number | undefined;
  /**
   * Mozgás értéke (quantity × unitCost, value-trail).
   * Opcionális — BE value-trail bővítés pending. Ha undefined, "—" renderelendő.
   * RENTAL típusnál szándékosan nem renderelendő.
   */
  value?: number | undefined;
}

export interface MovementFilters {
  search?: string;
  type?: string;
  page?: number;
  pageSize?: number;
}

// Backend movement type → frontend movement type mapping
const BACKEND_TYPE_MAP: Record<string, string> = {
  RECEIPT: 'IN',
  ISSUE: 'OUT',
  TRANSFER_IN: 'TRANSFER',
  TRANSFER_OUT: 'TRANSFER',
  ADJUSTMENT: 'ADJUSTMENT',
  RETURN: 'RETURN',
  SCRAP: 'OUT',
  RESERVATION: 'OUT',
  RELEASE: 'IN',
  STATUS_CHANGE: 'ADJUSTMENT',
};

interface BackendMovement {
  id: string;
  inventoryItemId?: string;
  productId?: string;
  type: string;
  quantityChange?: number;
  previousQuantity?: number;
  newQuantity?: number;
  referenceType?: string;
  referenceId?: string;
  reason?: string;
  performedBy?: string;
  performedAt?: string;
  createdAt: string;
  serialNumber?: string;
  unit?: string;
  unitCost?: number;
  value?: number;
}

function normalizeMovement(m: BackendMovement): StockMovementRecord {
  return {
    id: m.id,
    itemId: m.inventoryItemId ?? m.productId ?? '',
    itemName: m.serialNumber ?? m.productId ?? '-',
    itemSku: m.serialNumber ?? '-',
    type: BACKEND_TYPE_MAP[m.type] ?? 'ADJUSTMENT',
    quantity: m.quantityChange ?? 0,
    previousStock: m.previousQuantity ?? 0,
    newStock: m.newQuantity ?? 0,
    reference: m.referenceType
      ? `${m.referenceType}${m.referenceId ? ` #${m.referenceId.slice(0, 8)}` : ''}`
      : undefined,
    note: m.reason ?? undefined,
    createdBy: m.performedBy ?? '',
    createdByName: m.performedBy ?? '-',
    createdAt: m.performedAt ?? m.createdAt,
    unitCost: m.unitCost,
    value: m.value,
  };
}

/**
 * Get stock movement history
 */
export async function getInventoryMovements(
  filters: MovementFilters = {}
): Promise<{ data: StockMovementRecord[] }> {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.type) params.append('type', filters.type);
  if (filters.page !== undefined)
    params.append('offset', String(filters.page * (filters.pageSize ?? 50)));
  if (filters.pageSize !== undefined) params.append('limit', String(filters.pageSize));
  const query = params.toString();
  const response = await api.get<{
    data: { movements: BackendMovement[]; total: number } | BackendMovement[];
  }>(`/movements${query ? `?${query}` : ''}`);
  // Backend wraps in { data: { movements: [...], total } }
  const raw = response.data;
  const movements = Array.isArray(raw)
    ? raw
    : ((raw as { movements: BackendMovement[] }).movements ?? []);
  return { data: movements.map(normalizeMovement) };
}

// ============================================
// INVENTORY TRANSFER API (Q-TRF-1)
// ============================================

export interface TransferToRentalResult {
  rentalEquipmentId: string;
  fixedAssetId?: string;
}

export interface TransferToOwnAssetResult {
  fixedAssetId?: string;
}

/**
 * BE convert-to-asset response shape (transfer.controller `convertToAsset` → ConvertToAssetResult).
 * A FE a meglévő Transfer*Result típusokra mapeli (konzumens-stabilitás).
 */
interface ConvertToAssetApiResult {
  inventoryItemId: string;
  targetType: 'RENTAL_EQUIPMENT' | 'OWNED_ASSET';
  rentalEquipment: { id: string; equipmentCode: string } | null;
  fixedAsset: { id: string } | null;
  stockMovementId: string;
}

/**
 * Transfer inventory item to rental equipment (bérgépbe átvezetés)
 * RBAC: BOLTVEZETO+ only. Kanonikus BE-route: POST /inventory/transfers/convert-to-asset
 * (body-alapú targetType; a korábbi /inventory/:id/transfer/rental-equipment route DEAD volt → 404).
 */
export async function transferToRentalEquipment(
  id: string
): Promise<{ data: TransferToRentalResult }> {
  const res = await api.post<{ data: ConvertToAssetApiResult }>(
    '/inventory/transfers/convert-to-asset',
    { inventoryItemId: id, targetType: 'RENTAL_EQUIPMENT' }
  );
  return {
    data: {
      rentalEquipmentId: res.data.rentalEquipment?.id ?? '',
      ...(res.data.fixedAsset ? { fixedAssetId: res.data.fixedAsset.id } : {}),
    },
  };
}

/**
 * Transfer inventory item to own asset (saját eszközbe átvezetés)
 * RBAC: BOLTVEZETO+ only. Kanonikus BE-route: POST /inventory/transfers/convert-to-asset
 * (body-alapú targetType; a korábbi /inventory/:id/transfer/own-asset route DEAD volt → 404).
 */
export async function transferToOwnAsset(id: string): Promise<{ data: TransferToOwnAssetResult }> {
  const res = await api.post<{ data: ConvertToAssetApiResult }>(
    '/inventory/transfers/convert-to-asset',
    { inventoryItemId: id, targetType: 'OWNED_ASSET' }
  );
  return {
    data: {
      ...(res.data.fixedAsset ? { fixedAssetId: res.data.fixedAsset.id } : {}),
    },
  };
}

// ============================================
// WAREHOUSE-TO-WAREHOUSE TRANSFER (multi-warehouse Phase-1b)
// Házon-belüli átcsoportosítás (számla nélkül, 1 cég) — a kész BE
// warehouse.controller (POST /warehouses/transfers) bedrótozása.
// ============================================

export type WarehouseTransferStatus = 'PENDING' | 'IN_TRANSIT' | 'COMPLETED' | 'CANCELLED';

export interface WarehouseTransferItemInput {
  inventoryItemId: string;
  quantity: number;
  unit: string;
  serialNumber?: string;
  note?: string;
}

export interface CreateWarehouseTransferData {
  sourceWarehouseId: string;
  targetWarehouseId: string;
  reason?: string;
  items: WarehouseTransferItemInput[];
}

export interface WarehouseTransfer {
  id: string;
  status: WarehouseTransferStatus;
  sourceWarehouseId: string;
  targetWarehouseId: string;
  reason?: string;
  createdAt: string;
}

/**
 * Create warehouse-to-warehouse transfer (raktárközi átcsoportosítás).
 * Készlet-write: atomi forrás-dekrement + cél-inkrement (BE Prisma transaction).
 * RBAC: BOLTVEZETO+ only. A transfer PENDING státuszban jön létre.
 */
export async function createWarehouseTransfer(
  data: CreateWarehouseTransferData
): Promise<{ data: WarehouseTransfer }> {
  return api.post<{ data: WarehouseTransfer }>('/warehouses/transfers', data);
}

// ============================================
// INVENTORY GROUPS API (O-INV-2)
// ============================================

/**
 * Get inventory groups with RBAC visibility for current user
 */
export async function getInventoryGroups(): Promise<{ data: InventoryGroupInfo[] }> {
  return api.get<{ data: InventoryGroupInfo[] }>('/inventory/groups');
}

// ============================================
// INV-FG-7-1: Barcode Lookup API
// ============================================

export interface BarcodeLookupResult {
  inventoryItemId: string;
  productId: string;
  productName: string | null;
  productSku: string | null;
  barcode: string | null;
  quantity: number;
  status: InventoryStatus;
  unit: string;
  warehouseId: string;
  warehouseName: string | null;
  purchasePrice: number | null;
  sellingPrice: number | null;
}

/**
 * Lookup product by barcode, SKU, or serial number
 */
export async function lookupBarcode(barcode: string): Promise<{ data: BarcodeLookupResult }> {
  return api.get<{ data: BarcodeLookupResult }>(
    `/inventory/lookup-barcode/${encodeURIComponent(barcode)}`
  );
}

// ============================================
// INV-FG-7-2: Split Availability API
// ============================================

export interface SplitAvailabilityItem {
  receiptItemId: string;
  productId: string;
  productCode: string;
  productName: string;
  expectedQuantity: number;
  availableNow: number;
  backordered: number;
  status: 'AVAILABLE' | 'BACKORDERED' | 'PARTIAL';
}

export interface SplitAvailabilityResult {
  receiptId: string;
  items: SplitAvailabilityItem[];
  summary: {
    totalItems: number;
    fullyAvailable: number;
    fullyBackordered: number;
    partial: number;
  };
}

/**
 * Split receipt items into available vs backordered
 */
export async function splitReceiptAvailability(
  receiptId: string
): Promise<{ data: SplitAvailabilityResult }> {
  return api.post<{ data: SplitAvailabilityResult }>(
    `/receipts/${receiptId}/split-availability`,
    {}
  );
}

// ============================================
// INV-FG-7-3: Central Request API
// ============================================

export interface CentralRequest {
  id: string;
  productId: string | null;
  productName: string | null;
  productSku?: string | null;
  note: string | null;
  status: 'PENDING';
  requestedBy?: string;
  requestedAt: string;
}

/**
 * Create central warehouse request for low stock items
 */
export async function createCentralRequest(
  productIds: string[],
  note?: string
): Promise<{ data: { requests: CentralRequest[]; count: number } }> {
  return api.post<{ data: { requests: CentralRequest[]; count: number } }>(
    '/inventory/central-request',
    { productIds, note }
  );
}

/**
 * Get pending central warehouse requests
 */
export async function getCentralRequests(): Promise<{ data: CentralRequest[] }> {
  return api.get<{ data: CentralRequest[] }>('/inventory/central-requests');
}

// ============================================
// INV-FG-7-4: Phantom Resolution API
// ============================================

/**
 * Get count of phantom inventory items (qty=0 but status=AVAILABLE)
 */
export async function getPhantomCount(): Promise<{ data: { count: number } }> {
  return api.get<{ data: { count: number } }>('/inventory/phantom-count');
}

/**
 * Resolve phantom inventory item
 */
export async function resolvePhantom(
  id: string,
  action: 'SCRAP' | 'RECOUNT',
  actualQuantity?: number,
  reason?: string
): Promise<{ data: { id: string; action: string; newStatus?: string; newQuantity?: number } }> {
  return api.post<{
    data: { id: string; action: string; newStatus?: string; newQuantity?: number };
  }>(`/inventory/${id}/resolve-phantom`, { action, actualQuantity, reason });
}

// ============================================
// INV-FG-8-1: Dead Stock Analysis API
// ============================================

export type DeadStockClassification = 'ACTIVE' | 'WARNING' | 'DEAD';

export interface DeadStockItem {
  id: string;
  productName: string;
  sku: string;
  quantity: number;
  lastMovementDate: string | null;
  daysSinceMovement: number;
  classification: DeadStockClassification;
  estimatedValue: number;
  warehouseName: string;
}

export interface DeadStockSummary {
  activeCount: number;
  warningCount: number;
  deadCount: number;
  totalDeadValue: number;
}

export interface DeadStockAnalysis {
  items: DeadStockItem[];
  summary: DeadStockSummary;
}

/**
 * Get dead stock analysis — items classified by movement recency
 */
export async function getDeadStockAnalysis(
  thresholdDays?: number
): Promise<{ data: DeadStockAnalysis }> {
  const params = thresholdDays ? `?thresholdDays=${thresholdDays}` : '';
  return api.get<{ data: DeadStockAnalysis }>(`/inventory/dead-stock-analysis${params}`);
}

// ============================================
// INV-FG-8-2: Turnover Analysis (ITR) API
// ============================================

export interface TurnoverAnalysis {
  year: number;
  cogs: number;
  averageInventoryValue: number;
  turnoverRatio: number;
  previousYearRatio: number | null;
  trend: 'up' | 'down' | 'stable';
}

/**
 * Get ITR (Inventory Turnover Ratio) for a given year
 */
export async function getTurnoverAnalysis(year?: number): Promise<{ data: TurnoverAnalysis }> {
  const params = year ? `?year=${year}` : '';
  return api.get<{ data: TurnoverAnalysis }>(`/inventory/turnover-analysis${params}`);
}

// ============================================
// INV-FG-8-2: ABC Analysis API
// ============================================

export type AbcClass = 'A' | 'B' | 'C';

export interface AbcItem {
  productId: string;
  productName: string;
  sku: string;
  revenue: number;
  cumulativePercent: number;
  abcClass: AbcClass;
}

export interface AbcAnalysis {
  items: AbcItem[];
  summary: {
    aCount: number;
    bCount: number;
    cCount: number;
    totalRevenue: number;
  };
}

/**
 * Get ABC (Pareto) analysis — products classified by revenue contribution
 */
export async function getAbcAnalysis(): Promise<{ data: AbcAnalysis }> {
  return api.get<{ data: AbcAnalysis }>('/inventory/abc-analysis');
}

// ============================================
// INV-FG-8-3: Seasonality Analysis API
// ============================================

export type SeasonalTrend = 'PEAK' | 'NORMAL' | 'LOW';

export interface MonthlySeasonalData {
  month: number;
  avgQuantity: number;
  trend: SeasonalTrend;
}

export interface SeasonalityAnalysis {
  productId: string;
  productName: string;
  year: number;
  monthlyData: MonthlySeasonalData[];
  peakMonths: number[];
  lowMonths: number[];
  seasonalIndex: number;
}

export interface SeasonalityOverviewItem {
  productId: string;
  productName: string;
  sku: string;
  seasonalIndex: number;
  peakMonths: number[];
  lowMonths: number[];
}

/**
 * Get seasonality analysis for a single product
 */
export async function getSeasonalityAnalysis(
  productId: string,
  year?: number
): Promise<{ data: SeasonalityAnalysis }> {
  const params = new URLSearchParams({ productId });
  if (year) params.append('year', year.toString());
  return api.get<{ data: SeasonalityAnalysis }>(`/inventory/seasonality-analysis?${params}`);
}

/**
 * Get top 10 most seasonal products
 */
export async function getSeasonalityOverview(): Promise<{ data: SeasonalityOverviewItem[] }> {
  return api.get<{ data: SeasonalityOverviewItem[] }>('/inventory/seasonality-overview');
}

// ============================================
// INV-FG-8-4: Transfer Suggestions API
// ============================================

export interface TransferSuggestion {
  productId: string;
  productName: string;
  fromWarehouseId: string;
  fromWarehouseName: string;
  toWarehouseId: string;
  toWarehouseName: string;
  currentQtyFrom: number;
  currentQtyTo: number;
  suggestedQty: number;
  reason: string;
}

export interface TransferSuggestionResult {
  suggestions: TransferSuggestion[];
  generatedAt: string;
}

/**
 * Get transfer suggestions between warehouses
 */
export async function getTransferSuggestions(): Promise<{ data: TransferSuggestionResult }> {
  return api.get<{ data: TransferSuggestionResult }>('/inventory/transfer-suggestions');
}

// ============================================
// INV-FG-8-5: Supplier Catalog API
// ============================================

export interface SupplierInfo {
  code: string;
  name: string;
}

export interface SupplierProduct {
  sku: string;
  name: string;
  description: string;
  category: string;
  priceNet: number;
  currency: string;
  available: boolean;
  leadTimeDays: number;
}

export interface SupplierPriceResult {
  sku: string;
  priceNet: number;
  priceGross: number;
  currency: string;
  validUntil: string;
  minOrderQty: number;
}

/**
 * Get available supplier providers
 */
export async function getSupplierList(): Promise<{ data: SupplierInfo[] }> {
  return api.get<{ data: SupplierInfo[] }>('/inventory/supplier-catalog/suppliers');
}

/**
 * Search supplier product catalog
 */
export async function searchSupplierCatalog(
  supplier: string,
  query: string
): Promise<{ data: SupplierProduct[] }> {
  const params = new URLSearchParams({ supplier, query });
  return api.get<{ data: SupplierProduct[] }>(`/inventory/supplier-catalog/search?${params}`);
}

/**
 * Get supplier price for a product
 */
export async function getSupplierPrice(
  supplier: string,
  sku: string
): Promise<{ data: SupplierPriceResult }> {
  const params = new URLSearchParams({ supplier, sku });
  return api.get<{ data: SupplierPriceResult }>(`/inventory/supplier-catalog/price?${params}`);
}

// ============================================
// STOCK COUNT API (Epic 24, Q-T109 fázis-1: route + lista)
// A `/inventory/stock-count` route korábban a `/inventory/:id` catch-all-ba esett
// (":id"="stock-count") → rossz hívás → 400. A BE `GET /api/v1/stock-counts` kész
// (StockCountController#listStockCounts, #833 óta wired) — ez a dedikált FE-kliens.
// ============================================

export type StockCountStatus =
  | 'DRAFT'
  | 'IN_PROGRESS'
  | 'SUSPENDED'
  | 'PENDING_REVIEW'
  | 'PENDING_ADJUSTMENT'
  | 'COMPLETED'
  | 'CANCELLED';

export type StockCountType = 'FULL' | 'PARTIAL_CATEGORY' | 'PARTIAL_ZONE' | 'CYCLE' | 'SPOT';

/**
 * Leltár (Stock Count) — minimál FE-típus a BE `GET /api/v1/stock-counts` válaszból
 * (`{ data: IStockCount[] }`, lásd `packages/store/stock-count/src/interfaces/stock-count.interface.ts`).
 * A BE repository (`findByFilter`) jelenleg NEM ad warehouseName/locationName join-t,
 * csak nyers ID-kat — measured-honesty, a FE nyers ID-t renderel amíg a BE read-model
 * nem bővül raktár-névvel.
 */
export interface StockCount {
  id: string;
  tenantId: string;
  locationId: string;
  warehouseId: string;
  countNumber: string;
  type: StockCountType;
  status: StockCountStatus;
  name: string;
  scheduledStartDate: string;
  scheduledEndDate: string;
  actualStartDate?: string | undefined;
  actualEndDate?: string | undefined;
  stockFrozen: boolean;
  responsibleUserId: string;
  categoryIds?: string[] | undefined;
  zoneIds?: string[] | undefined;
  totalItems: number;
  countedItems: number;
  varianceCount: number;
  notes?: string | undefined;
  createdAt: string;
  updatedAt: string;
}

export interface StockCountFilters {
  status?: StockCountStatus;
  warehouseId?: string;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface StockCountListResponse {
  data: StockCount[];
}

/**
 * Get list of stock counts (leltárak) with filters.
 * BE: GET /api/v1/stock-counts (StockCountController#listStockCounts).
 */
export async function getStockCounts(
  filters: StockCountFilters = {}
): Promise<StockCountListResponse> {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.warehouseId) params.append('warehouseId', filters.warehouseId);
  if (filters.locationId) params.append('locationId', filters.locationId);
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.append('dateTo', filters.dateTo);
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
  if (filters.offset !== undefined) params.append('offset', filters.offset.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/stock-counts?${queryString}` : '/stock-counts';

  return api.get<StockCountListResponse>(endpoint);
}
